import { FC, Fragment, ReactNode } from 'react';

//* 릴리즈 노트가 쓰는 문법만 처리하는 최소 렌더러.
//* 노트는 우리가 템플릿으로 쓰는 글이라 문법이 한정적이고, 이걸 위해
//* react-markdown(remark 계열 의존성 다수)을 들이지 않는다.
//* 지원: ## / ### 제목, - 목록, --- 구분선, > 인용, **굵게**, [캡처: ...] 자리표시자

interface Props {
  body: string;
}

//* **굵게**만 인라인 처리 (그 외 문자는 그대로)
const renderInline = (text: string): ReactNode => {
  const chunks = text.split('**');
  return chunks.map((chunk, index) =>
    index % 2 === 1 ? (
      <strong key={index} className="font-bold">
        {chunk}
      </strong>
    ) : (
      <Fragment key={index}>{chunk}</Fragment>
    ),
  );
};

const isCaptureLine = (line: string) => /^\[캡처:/.test(line.trim());

export const MiniMarkdown: FC<Props> = function MiniMarkdown({ body }) {
  const lines = body.split('\n');
  const blocks: ReactNode[] = [];

  //* 연속된 `- ` 줄을 하나의 목록으로 묶기 위한 버퍼
  let listBuffer: string[] = [];
  const flushList = () => {
    if (listBuffer.length === 0) return;
    const items = [...listBuffer];
    listBuffer = [];
    blocks.push(
      <ul key={`ul-${blocks.length}`} className="ml-4 list-disc space-y-1">
        {items.map((item, index) => (
          <li key={index} className="text-sm leading-6">
            {renderInline(item)}
          </li>
        ))}
      </ul>,
    );
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trimEnd();
    const trimmed = line.trim();

    if (trimmed.startsWith('- ')) {
      listBuffer.push(trimmed.slice(2));
      return;
    }
    flushList();

    if (trimmed === '') return;

    if (trimmed === '---') {
      blocks.push(<hr key={blocks.length} className="my-4 theme-border-1" />);
      return;
    }

    if (trimmed.startsWith('### ')) {
      blocks.push(
        <h4 key={blocks.length} className="pt-2 text-sm font-bold">
          {renderInline(trimmed.slice(4))}
        </h4>,
      );
      return;
    }

    if (trimmed.startsWith('## ')) {
      blocks.push(
        <h3 key={blocks.length} className="pt-4 text-base font-bold">
          {renderInline(trimmed.slice(3))}
        </h3>,
      );
      return;
    }

    if (trimmed.startsWith('# ')) {
      blocks.push(
        <h2 key={blocks.length} className="pt-4 text-lg font-bold">
          {renderInline(trimmed.slice(2))}
        </h2>,
      );
      return;
    }

    if (isCaptureLine(trimmed)) {
      //* 디자이너가 채울 캡처 자리. 관리자 화면에서는 "뭘 찍어야 하는지"가
      //* 그대로 정보라서 숨기지 않고 흐리게 노출한다.
      blocks.push(
        <div
          key={blocks.length}
          className="rounded border border-dashed px-3 py-2 text-xs theme-text-8 theme-border-1"
        >
          {trimmed}
        </div>,
      );
      return;
    }

    if (trimmed.startsWith('> ')) {
      blocks.push(
        <blockquote
          key={blocks.length}
          className="border-l-2 pl-3 text-sm leading-6 theme-text-8 theme-border-1"
        >
          {renderInline(trimmed.slice(2))}
        </blockquote>,
      );
      return;
    }

    blocks.push(
      <p key={blocks.length} className="text-sm leading-6">
        {renderInline(trimmed)}
      </p>,
    );
  });

  flushList();

  return <div className="space-y-2">{blocks}</div>;
};
